import React, { useState } from 'react';
import { X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { createForm } from "../../../services/formService";

interface CreateFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFormCreated: (formId: string, formSlug: string) => void;
}

const CreateFormModal: React.FC<CreateFormModalProps> = ({ isOpen, onClose, onFormCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [format, setFormat] = useState<'form' | 'wizard'>('form');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('Please enter a form name');
            return;
        }

        setIsSubmitting(true);
        try {
            // Generate robust slug
            const nameSlug = name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

            const newForm = await createForm({
                name,
                nameSlug,
                description,
                version: '0.1'
            });

            // Store format in a way that FormBuilderPage can retrieve it initially if needed
            // Or we can initialize the form content right here if necessary.
            // For now, we'll let the builder page handle content initialization, 
            // but passing format might be needed if we want to start with wizard.

            toast.success('Form created successfully');

            onFormCreated(newForm.id, newForm.nameSlug!);
            onClose();

            // Reset form
            setName('');
            setDescription('');
            setFormat('form');
        } catch (error) {
            console.error('Error creating form:', error);
            toast.error('Failed to create form');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                            <FileText className="text-indigo-600" size={20} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">Create New Form</h3>
                            <p className="text-xs text-gray-500">Set up your form details</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Form Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Employee Onboarding"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of this form..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Format
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setFormat('form')}
                                className={`px - 3 py - 2 text - sm font - medium rounded - lg border ${format === 'form'
                                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                    } `}
                            >
                                Standard Form
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormat('wizard')}
                                className={`px - 3 py - 2 text - sm font - medium rounded - lg border ${format === 'wizard'
                                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                    } `}
                            >
                                Wizard (Multi-step)
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Form'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateFormModal;
